import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers, User, grantPromotion, getPromotions, Promotion, Page, getPlans, Plan, grantSubscription, getUserSubscriptionHistory, revokeSubscription, getRelayTrafficStats, RelayTrafficStats } from '../api/admin';
import styles from './CommonPage.module.css';
import { Gift, Monitor, Search, ChevronLeft, ChevronRight, X, Clock, AlertTriangle, Edit, Activity } from 'lucide-react';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Page<User> | null>(null);
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [message, setMessage] = useState('');

  // Extra quota modal states
  const [showExtraQuotaModal, setShowExtraQuotaModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [extraQuotaGB, setExtraQuotaGB] = useState('');

  // Filter states
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState('id,desc');
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchPromotions();
    fetchPlans();
  }, [page, sort]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const result = await getUsers(page, 10, keyword, sort);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch users', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const fetchPromotions = async () => {
    try {
      const result = await getPromotions(0, 1000); // Fetch all (up to 1000) for selection list
      setPromotions(result.content);
    } catch (error) {
      console.error('Failed to fetch promotions', error);
    }
  };

  const handleGrantPromotion = async (promotionId: number) => {
    if (!selectedUser) return;

    if (selectedUser.activePlanName) {
      if (!window.confirm(`이미 [${selectedUser.activePlanName}]을 구독 중입니다. 기존 구독을 종료하고 새로 지급하시겠습니까? (기존 구독은 비활성화되며 기록에서 확인 가능합니다)`)) {
        return;
      }
    }

    try {
      await grantPromotion(selectedUser.id, promotionId);
      setMessage(`${selectedUser.username}님에게 프로모션이 지급되었습니다.`);
      setShowPromoModal(false);
      setTimeout(() => setMessage(''), 3000);
      fetchUsers(); // Refresh list to show new plan
    } catch (error) {
      setMessage('프로모션 지급에 실패했습니다.');
    }
  };

  const fetchPlans = async () => {
    try {
      const result = await getPlans(0, 1000); // Fetch all (up to 1000) for selection list
      setPlans(result.content);
    } catch (error) {
      console.error('Failed to fetch plans', error);
    }
  };

  const handleGrantSubscription = async (planId: number) => {
    if (!selectedUser) return;

    if (selectedUser.activePlanName) {
      if (!window.confirm(`이미 [${selectedUser.activePlanName}]을 구독 중입니다. 기존 구독을 종료하고 새로 지급하시겠습니까? (기존 구독은 비활성화되며 기록에서 확인 가능합니다)`)) {
        return;
      }
    }

    try {
      await grantSubscription(selectedUser.id, planId);
      setMessage(`${selectedUser.username}님에게 구독권이 지급되었습니다.`);
      setShowPromoModal(false);
      setTimeout(() => setMessage(''), 3000);
      fetchUsers(); // Refresh list to show new plan
    } catch (error) {
      setMessage('구독권 지급에 실패했습니다.');
    }
  };

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNext = () => {
    if (data && page < data.totalPages - 1) setPage(page + 1);
  };

  // User History Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const handleShowHistory = async (user: User) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const history = await getUserSubscriptionHistory(user.id);
      setHistoryData(history);
    } catch (e) {
      console.error(e);
      alert('이력 조회 실패');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevoke = async (subscriptionId: number) => {
    if (!window.confirm("정말 이 구독/프로모션을 비활성화(중지) 하시겠습니까? 사용자 권한이 즉시 회수될 수 있습니다.")) {
      return;
    }

    try {
      await revokeSubscription(subscriptionId);
      alert('비활성화 되었습니다.');
      // Refresh history and user list
      if (selectedUser) handleShowHistory(selectedUser);
      fetchUsers();
    } catch (e) {
      console.error(e);
      alert('비활성화 실패');
    }
  };

  // Edit User Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phoneNumber: '',
    companyName: '',
    businessRegistrationNumber: '',
    enabled: true,
  });

  // Traffic Stats Modal State
  const [showTrafficModal, setShowTrafficModal] = useState(false);
  const [trafficStats, setTrafficStats] = useState<RelayTrafficStats | null>(null);
  const [trafficLoading, setTrafficLoading] = useState(false);

  const handleShowTrafficStats = async (user: User) => {
    setSelectedUser(user);
    setShowTrafficModal(true);
    setTrafficLoading(true);
    try {
      const stats = await getRelayTrafficStats(user.username);
      setTrafficStats(stats);
    } catch (e) {
      console.error(e);
      alert('트래픽 조회 실패');
    } finally {
      setTrafficLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showPromoModal || showHistoryModal || showEditModal || showTrafficModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showPromoModal, showHistoryModal, showEditModal, showTrafficModal]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      phoneNumber: user.phoneNumber || '',
      companyName: user.companyName || '',
      businessRegistrationNumber: user.businessRegistrationNumber || '',
      enabled: user.enabled,
    });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    try {
      const { updateUser } = await import('../api/admin');
      await updateUser(selectedUser.id, editForm);
      alert('수정되었습니다.');
      setShowEditModal(false);
      fetchUsers();
    } catch (e) {
      console.error(e);
      alert('수정 실패');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.wrapper}>
          <h1>회원 관리</h1>
          <p>등록된 사용자 및 라이선스를 관리합니다.</p>
        </div>
      </div>

      {message && <div style={{ marginBottom: '16px', padding: '12px', background: '#dcfce7', color: '#166534', borderRadius: '6px' }}>{message}</div>}

      <div className={styles.card} style={{ marginBottom: '24px' }}>
        <div className={styles.cardAboveBox} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
            <div className={styles.inputBox} style={{ position: 'relative', width: '300px' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                placeholder="아이디 검색..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <button type="submit" className={styles.primaryBtn}>검색</button>
          </form>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(0); }}
            style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white' }}
          >
            <option value="id,desc">최신순 (ID)</option>
            <option value="id,asc">오래된순 (ID)</option>
            <option value="username,asc">이름순 A-Z</option>
            <option value="username,desc">이름순 Z-A</option>
          </select>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>사용자 ID</th>
              <th>정보 / 연락처</th>
              <th>가입일시 / 최근 로그인</th>
              <th>사업자 정보</th>
              <th>회원 상태</th>
              <th>사용 여부</th>
              <th>기기 제한</th>
              <th>현재 플랜</th>
              <th>만료일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px' }}>로딩 중...</td></tr>
            ) : data?.content.map((user) => (
              <tr key={user.id}>
                <td data-label="ID">{user.id}</td>
                <td data-label="사용자 ID">
                  <div style={{ fontWeight: 500 }}>{user.username}</div>
                  {user.email && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{user.email}</div>}
                </td>
                <td data-label="정보 / 연락처">
                  <div style={{ fontSize: '0.9rem' }}>{user.name || '-'}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {user.phoneNumber || '-'}
                    {user.phoneVerified && (
                      <span style={{
                        padding: '1px 4px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                        backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0'
                      }}>
                        인증완료
                      </span>
                    )}
                  </div>
                </td>
                <td data-label="가입일시 / 최근 로그인">
                  <div style={{ fontSize: '0.8rem' }}>
                    <span style={{ color: '#6b7280' }}>가입: </span>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ko-KR') : '-'}
                  </div>
                  <div style={{ fontSize: '0.8rem' }}>
                    <span style={{ color: '#6b7280' }}>로그인: </span>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ko-KR') : '-'}
                  </div>
                </td>
                <td data-label="사업자 정보">
                  {user.companyName ? (
                    <>
                      <div style={{ fontSize: '0.9rem' }}>{user.companyName}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{user.businessRegistrationNumber}</div>
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                  )}
                </td>
                <td data-label="회원 상태">
                  {user.status === 'WITHDRAWING' ? (
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                      backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d',
                      display: 'inline-block'
                    }}>
                      탈퇴 유예
                    </span>
                  ) : user.status === 'WITHDRAWN' ? (
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                      backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb',
                      display: 'inline-block'
                    }}>
                      탈퇴 완료
                    </span>
                  ) : (
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                      backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                      display: 'inline-block'
                    }}>
                      정상 회원
                    </span>
                  )}

                  {/* Withdrawal Date Info - Only for Withdrawing */}
                  {user.status === 'WITHDRAWING' && user.withdrawalRequestedAt && (
                    <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: '4px' }}>
                      {new Date(user.withdrawalRequestedAt).toLocaleDateString()} 요청
                    </div>
                  )}
                </td>
                <td data-label="사용 여부">
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                    backgroundColor: user.enabled ? '#dcfce7' : '#fee2e2',
                    color: user.enabled ? '#166534' : '#991b1b'
                  }}>
                    {user.enabled ? '활성' : '비활성'}
                  </span>
                </td>
                <td data-label="기기 제한">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: 600 }}>{user.deviceLimit || 1}</span>
                    <button
                      className={styles.iconBtn}
                      style={{ padding: '2px', width: '20px', height: '20px' }}
                      onClick={() => {
                        const newLimit = prompt('변경할 기기 제한 수를 입력하세요:', (user.deviceLimit || 1).toString());
                        if (newLimit) {
                          const limit = parseInt(newLimit, 10);
                          if (!isNaN(limit) && limit > 0) {
                            import('../api/admin').then(({ updateUserDeviceLimit }) => {
                              updateUserDeviceLimit(user.id, limit)
                                .then(() => {
                                  alert('변경되었습니다.');
                                  fetchUsers();
                                })
                                .catch(() => alert('변경 실패'));
                            });
                          } else {
                            alert('유효한 숫자를 입력해주세요.');
                          }
                        }
                      }}
                      title="제한 수 변경"
                    >
                      <Edit size={12} />
                    </button>
                  </div>
                </td>
                <td data-label="현재 플랜">
                  {user.activePlanName ? (
                    <span style={{ fontWeight: 600, color: '#2563eb' }}>{user.activePlanName}</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                  )}
                </td>
                <td data-label="만료일">
                  {user.expiryDate ? (
                    <span style={{ fontSize: '0.875rem' }}>{new Date(user.expiryDate).toLocaleDateString()}</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                  )}
                </td>
                <td data-label="관리">
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => handleShowHistory(user)}
                      title="기록"
                      style={{ background: '#f3f4f6', color: '#374151', padding: '6px 12px', fontSize: '0.875rem' }}
                    >
                      구독 / 프로모션 이력
                    </button>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => navigate(`/devices?keyword=${user.username}`)}
                      title="기기 보기"
                      style={{ background: '#e0e7ff', color: '#3730a3', padding: '6px 12px', fontSize: '0.875rem' }}
                    >
                      기기 관리
                    </button>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => handleEditUser(user)}
                      title="정보 수정"
                      style={{ background: '#ffedd5', color: '#9a3412', padding: '6px 12px', fontSize: '0.875rem' }}
                    >
                      정보 수정
                    </button>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => handleShowTrafficStats(user)}
                      title="트래픽 통계"
                      style={{ background: '#dbeafe', color: '#1e40af', padding: '6px 12px', fontSize: '0.875rem' }}
                    >
                      <Activity size={14} style={{ marginRight: '4px' }} />
                      트래픽
                    </button>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => {
                        setSelectedUser(user);
                        setShowPromoModal(true);
                      }}
                      title="프로모션 지급"
                      style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                    >
                      구독 / 프로모션 부여
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {data?.content.length === 0 && !loading && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>검색된 사용자가 없습니다.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <button onClick={handlePrev} disabled={page === 0}>
          <ChevronLeft size={16} /> 이전
        </button>
        <span>
          {data?.number !== undefined ? data.number + 1 : 1} / {data?.totalPages || 1} 페이지
        </span>
        <button onClick={handleNext} disabled={!data || page >= data.totalPages - 1}>
          다음 <ChevronRight size={16} />
        </button>
      </div>

      {showHistoryModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} style={{ maxWidth: '800px', width: '90%' }}>
            <div className={styles.modalHeader}>
              <h3>{selectedUser.username}님의 구독 이력</h3>
              <button onClick={() => setShowHistoryModal(false)} className={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {historyLoading ? (
                <div>로딩 중...</div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>시작일</th>
                        <th>종료일</th>
                        <th>상품명</th>
                        <th>유형</th>
                        <th>사용량 / 할당량</th>
                        <th>상태 / 관리</th>
                        <th>생성일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map(item => (
                        <tr key={item.id}>
                          <td>{new Date(item.startDate).toLocaleDateString()}</td>
                          <td>{new Date(item.endDate).toLocaleDateString()}</td>
                          <td>{item.sourceName}</td>
                          <td>
                            {item.sourceType === 'PAYMENT' ? '구독' : '프로모션'}
                          </td>
                          <td>
                            {item.enabled ? (
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {formatBytes(item.currentUsage || 0)}
                                  <span style={{ margin: '0 4px', color: '#9ca3af' }}>/</span>
                                  {formatBytes((item.limitBytes || 0) + (item.extraLimitBytes || 0))}
                                </div>
                                {item.extraLimitBytes && item.extraLimitBytes > 0 ? (
                                  <div style={{ fontSize: '0.75rem', color: '#2563eb' }}>
                                    (+ 관리자 추가 {formatBytes(item.extraLimitBytes)})
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <span style={{
                                padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                                backgroundColor: item.enabled ? '#dcfce7' : '#f3f4f6',
                                color: item.enabled ? '#166534' : '#6b7280',
                                width: 'fit-content'
                              }}>
                                {item.enabled ? '활성' : '비활성'}
                              </span>
                              {item.enabled && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  <button
                                    onClick={() => {
                                      setSelectedSubscriptionId(item.id);
                                      setExtraQuotaGB('');
                                      setShowExtraQuotaModal(true);
                                    }}
                                    style={{
                                      border: '1px solid #3b82f6',
                                      background: '#eff6ff',
                                      color: '#1e40af',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                      borderRadius: '6px',
                                      padding: '4px 10px',
                                      fontWeight: 500,
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#dbeafe';
                                      e.currentTarget.style.borderColor = '#2563eb';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#eff6ff';
                                      e.currentTarget.style.borderColor = '#3b82f6';
                                    }}
                                  >
                                    + 용량 추가
                                  </button>
                                  <button
                                    onClick={() => handleRevoke(item.id)}
                                    style={{
                                      border: '1px solid #fca5a5',
                                      background: '#fef2f2',
                                      color: '#dc2626',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                      borderRadius: '6px',
                                      padding: '4px 10px',
                                      fontWeight: 500,
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#fee2e2';
                                      e.currentTarget.style.borderColor = '#ef4444';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#fef2f2';
                                      e.currentTarget.style.borderColor = '#fca5a5';
                                    }}
                                  >
                                    비활성화
                                  </button>
                                </div>
                              )}
                              {!item.enabled && item.disabledAt && (
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                  ({new Date(item.disabledAt).toLocaleDateString()} 중단)
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{new Date(item.createdAt).toLocaleString()}</td>
                        </tr>
                      ))}
                      {historyData.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>이력이 없습니다.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPromoModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>{selectedUser?.username}님에게 플랜 지급</h3>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '8px' }}>프로모션</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {promotions.map(promo => (
                  <button
                    key={promo.id}
                    onClick={() => handleGrantPromotion(promo.id)}
                    className={styles.primaryBtn}
                    style={{ justifyContent: 'center', backgroundColor: '#4b5563', fontSize: '0.9rem' }}
                  >
                    {promo.name} ({promo.durationDays}일)
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 style={{ marginBottom: '8px' }}>정기 구독 (유료 플랜)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {plans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => handleGrantSubscription(plan.id)}
                    className={styles.primaryBtn}
                    style={{ justifyContent: 'center', backgroundColor: '#2563eb', fontSize: '0.9rem' }}
                  >
                    {plan.name} ({plan.price.toLocaleString()}원)
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => setShowPromoModal(false)} className={styles.cancelBtn} style={{ marginTop: '24px', width: '100%' }}>
              취소
            </button>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>{selectedUser.username} 정보 수정</h3>
            <div className={styles.formGroup} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>이름</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div className={styles.formGroup} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>연락처</label>
              <input
                type="text"
                value={editForm.phoneNumber}
                onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div className={styles.formGroup} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>상호명 (법인명)</label>
              <input
                type="text"
                value={editForm.companyName}
                onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div className={styles.formGroup} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', fontWeight: 500 }}>사업자등록번호</label>
              <input
                type="text"
                value={editForm.businessRegistrationNumber}
                onChange={(e) => setEditForm({ ...editForm, businessRegistrationNumber: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              />
            </div>

            <div className={styles.formGroup} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={editForm.enabled}
                  onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                계정 활성화 (기기 로그인 허용)
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={handleSaveUser} className={styles.primaryBtn} style={{ flex: 1, justifyContent: 'center' }}>저장</button>
              <button onClick={() => setShowEditModal(false)} className={styles.cancelBtn} style={{ flex: 1 }}>취소</button>
            </div>
          </div>
        </div>
      )}
      {showTrafficModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Relay 트래픽 통계</h3>
              <button onClick={() => setShowTrafficModal(false)} className={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>

            {trafficLoading ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>로딩 중...</div>
            ) : trafficStats ? (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#1e40af', marginBottom: '8px', fontWeight: 600 }}>이번 주 이용량</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#172554' }}>
                      {formatBytes(trafficStats.weeklyBytesSent)}
                    </div>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f5f3ff',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#5b21b6', marginBottom: '8px', fontWeight: 600 }}>이번 달 이용량</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#4c1d95' }}>
                      {formatBytes(trafficStats.monthlyBytesSent)}
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: '0.85rem', color: '#6b7280', textAlign: 'center' }}>
                  * 데이터는 실시간으로 집계되며, 약간의 지연이 있을 수 있습니다.
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                통계 데이터를 불러올 수 없습니다.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Extra Quota Modal */}
      {showExtraQuotaModal && selectedSubscriptionId && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} style={{ maxWidth: '400px' }}>
            <div className={styles.modalHeader}>
              <h3>추가 용량 부여</h3>
              <button onClick={() => {
                setShowExtraQuotaModal(false);
                setExtraQuotaGB('');
                setSelectedSubscriptionId(null);
              }} className={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  추가할 용량 (GB)
                </label>
                <input
                  type="number"
                  value={extraQuotaGB}
                  onChange={(e) => setExtraQuotaGB(e.target.value)}
                  placeholder="예: 0.5"
                  min="0.01"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  autoFocus
                />
                <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                  소수점 입력 가능 (예: 0.5GB = 500MB)
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowExtraQuotaModal(false);
                    setExtraQuotaGB('');
                    setSelectedSubscriptionId(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    color: '#374151'
                  }}
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    const gb = parseFloat(extraQuotaGB);

                    if (isNaN(gb) || gb <= 0) {
                      alert('올바른 숫자를 입력해주세요.');
                      return;
                    }

                    const bytes = Math.round(gb * 1024 * 1024 * 1024);

                    try {
                      const { grantExtraLimit } = await import('../api/admin');
                      await grantExtraLimit(selectedSubscriptionId, bytes);
                      alert(`${gb}GB가 추가되었습니다.`);
                      setShowExtraQuotaModal(false);
                      setExtraQuotaGB('');
                      setSelectedSubscriptionId(null);
                      if (selectedUser) handleShowHistory(selectedUser);
                    } catch (e: any) {
                      console.error(e);
                      alert('실패했습니다: ' + (e.response?.data || e.message));
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  추가
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
